import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { FRONT_URL } from "../../utils/constants";
import { FaTimes } from "react-icons/fa";

const slideVariants = {
  enter: (direction) => ({
    x: direction * 90,
    opacity: 0,
    scale: 0.97,
  }),
  center: { x: 0, opacity: 1, scale: 1 },
  exit: (direction) => ({
    x: direction * -90,
    opacity: 0,
    scale: 0.97,
  }),
};

const ImageModal = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  const prevIndex = useRef(currentIndex);
  const dirRef = useRef(1);

  if (currentIndex !== prevIndex.current) {
    const len = images.length;
    dirRef.current = currentIndex === (prevIndex.current + 1) % len ? 1 : -1;
    prevIndex.current = currentIndex;
  }
  const direction = dirRef.current;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" || e.key === "d") onNext();
      if (e.key === "ArrowLeft" || e.key === "a") onPrev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onNext, onPrev]);

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
    >
      <Stage onClick={(e) => e.stopPropagation()}>
        <AnimatePresence
          custom={direction}
          initial={false}
          mode="popLayout"
        >
          <Image
            key={currentIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: [0.22, 0.72, 0.18, 1] }}
            src={`${FRONT_URL}/images/${images?.[currentIndex]?.photoName}`}
            alt="preview"
            draggable={false}
          />
        </AnimatePresence>
      </Stage>

      {images.length > 1 && (
        <>
          <NavButton
            $left
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
          >
            <FiChevronLeft />
          </NavButton>
          <NavButton
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
          >
            <FiChevronRight />
          </NavButton>
        </>
      )}

      <Counter>
        {currentIndex + 1} / {images.length}
      </Counter>

      <CloseButton
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <FaTimes />
      </CloseButton>
    </Overlay>
  );
};

export default ImageModal;

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(6, 6, 8, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Stage = styled.div`
  position: relative;
  width: min(90vw, 100rem);
  height: 86vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Image = styled(motion.img)`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 0.6rem;
  user-select: none;
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 4.6rem;
  height: 4.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 16, 19, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(254, 95, 85, 0.4);
  border-radius: 50%;
  color: #fff;
  font-size: 2.2rem;
  cursor: pointer;
  z-index: 10000;
  transition: all 0.25s var(--ease, ease);

  &:hover {
    background: var(--red, #fe5f55);
    color: var(--bg0, #0a0a0c);
  }

  ${({ $left }) => ($left ? "left: 2rem;" : "right: 2rem;")}
`;

const Counter = styled.div`
  position: absolute;
  bottom: 2.4rem;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1.2rem;
  letter-spacing: 0.2em;
  font-family: "Poppins-Medium";
  color: var(--muted, #8e8c94);
  z-index: 10000;
  user-select: none;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2.5rem;
  right: 2.5rem;
  background: rgba(16, 16, 19, 0.6);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: #fff;
  border-radius: 50%;
  font-size: 1.6rem;
  width: 3.6rem;
  height: 3.6rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  transition: all 0.25s var(--ease, ease);
  z-index: 10000;

  &:hover {
    background: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
    color: var(--bg0, #0a0a0c);
  }
`;
